import * as Sentry from "@sentry/react";
import { createGStore } from "create-gstore";
import { useState } from "react";

import { publicFetchClient } from "@/shared/api/instance";
import { queryClient } from "@/shared/api/query-client";

import { ROUTES } from "./routes";
import { broadcastLogout, initSessionSync } from "./session-sync";

type AuthTokens = {
  accessToken: string;
  client: string;
  uid: string;
  expiry?: string;
  refreshToken?: string;
};

const ACCESS_TOKEN_KEY = "monument_access_token";
const CLIENT_KEY = "monument_client";
const UID_KEY = "monument_uid";
const REFRESH_TOKEN_KEY = "monument_refresh_token";
const EXPIRY_KEY = "monument_expiry";
const TOKEN_EXPIRY_BUFFER_MS = 60 * 1000; // 1 минута buffer

let refreshPromise: Promise<AuthTokens | null> | null = null;
let pendingRefreshCallbacks: Array<
  (result: Promise<AuthTokens | null>) => void
> = [];
let refreshAbortController: AbortController | null = null;

const isTokenExpiringSoon = (): boolean => {
  const expiry = localStorage.getItem(EXPIRY_KEY);
  if (!expiry) return false;
  const expiryMs = parseInt(expiry, 10) * 1000;
  return Date.now() + TOKEN_EXPIRY_BUFFER_MS >= expiryMs;
};

export const useSession = createGStore(() => {
  const [accessToken, setAccessToken] = useState<string | null>(() => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  });

  const [client, setClient] = useState<string | null>(() => {
    return localStorage.getItem(CLIENT_KEY);
  });

  const [uid, setUid] = useState<string | null>(() => {
    return localStorage.getItem(UID_KEY);
  });

  const [refreshToken, setRefreshToken] = useState<string | null>(() => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  });

  const login = (tokens: AuthTokens) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    if (tokens.client) {
      localStorage.setItem(CLIENT_KEY, tokens.client);
    }
    if (tokens.uid) {
      localStorage.setItem(UID_KEY, tokens.uid);
    }
    if (tokens.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    }
    if (tokens.expiry) {
      localStorage.setItem(EXPIRY_KEY, tokens.expiry);
    }

    setAccessToken(tokens.accessToken);
    setClient(tokens.client);
    setUid(tokens.uid);
    setRefreshToken(tokens.refreshToken ?? null);

    Sentry.setUser({ id: tokens.uid, email: tokens.uid });
  };

  const logout = (shouldRedirect = true) => {
    // Отмена ongoing refresh
    if (refreshAbortController) {
      refreshAbortController.abort();
      refreshAbortController = null;
    }

    // Отмена pending callbacks
    pendingRefreshCallbacks = [];
    refreshPromise = null;

    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(CLIENT_KEY);
    localStorage.removeItem(UID_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(EXPIRY_KEY);

    setAccessToken(null);
    setClient(null);
    setUid(null);
    setRefreshToken(null);

    Sentry.setUser(null);

    // Очищаем React Query кеш
    queryClient.clear();

    // Уведомляем другие вкладки
    broadcastLogout();

    // Редирект на страницу логина
    if (shouldRedirect && typeof window !== "undefined") {
      window.location.href = ROUTES.LOGIN;
    }
  };

  // Read directly from localStorage for accurate auth state
  const isAuthenticated = Boolean(
    localStorage.getItem(ACCESS_TOKEN_KEY) &&
      localStorage.getItem(CLIENT_KEY) &&
      localStorage.getItem(UID_KEY),
  );

  const getAuthHeaders = (): Record<string, string> | null => {
    // Read directly from localStorage to avoid stale state issues
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    const clientValue = localStorage.getItem(CLIENT_KEY);
    const uidValue = localStorage.getItem(UID_KEY);

    if (!token || !clientValue || !uidValue) return null;

    // Proactive refresh при истечении (fire-and-forget)
    if (isTokenExpiringSoon() && !refreshPromise) {
      refreshTokens().catch(() => {});
    }

    return {
      "access-token": token,
      client: clientValue,
      uid: uidValue,
    };
  };

  const refreshTokens = async (): Promise<AuthTokens | null> => {
    // Если уже идет refresh, ждём результат
    if (refreshPromise) {
      return new Promise((resolve) => {
        pendingRefreshCallbacks.push(resolve);
      });
    }

    const currentRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!currentRefreshToken) {
      logout();
      return null;
    }

    refreshAbortController = new AbortController();

    refreshPromise = publicFetchClient
      .POST("/api/v1/tokens", {
        body: { refresh_token: currentRefreshToken },
        signal: refreshAbortController.signal,
      })
      .then((response) => {
        if (response.error) {
          logout();
          return null;
        }

        // Get tokens from response headers
        const headers = response.response?.headers;
        const accessToken = headers?.get("access-token");
        const client = headers?.get("client");
        const uid = headers?.get("uid");
        const expiry = headers?.get("expiry");

        // Get refresh token from response body
        const data = response.data;

        if (accessToken && client && uid && data?.refresh_token) {
          const newTokens: AuthTokens = {
            accessToken,
            client,
            uid,
            expiry: expiry ?? undefined,
            refreshToken: data.refresh_token,
          };
          login(newTokens);
          return newTokens;
        }

        logout();
        return null;
      })
      .catch(() => {
        logout();
        return null;
      })
      .finally(() => {
        refreshAbortController = null;
        // Notify all pending callbacks with the result
        const result = refreshPromise!;
        refreshPromise = null;
        pendingRefreshCallbacks.forEach((callback) => callback(result));
        pendingRefreshCallbacks = [];
      });

    return refreshPromise;
  };

  return {
    isAuthenticated,
    isTokenExpiringSoon,
    login,
    logout,
    refreshTokens,
    getAuthHeaders,
    tokens: accessToken ? { accessToken, client, uid, refreshToken } : null,
  };
});

// Инициализация cross-tab синхронизации
initSessionSync(() => {
  // Silent logout без broadcast (чтобы не зациклиться)
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(CLIENT_KEY);
  localStorage.removeItem(UID_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(EXPIRY_KEY);

  // Очищаем query cache и редирект
  queryClient.clear();

  if (typeof window !== "undefined") {
    window.location.href = ROUTES.LOGIN;
  }
});
